import { createContext, useContext, useEffect, useRef, useState, useEffectEvent } from 'react'
import type { User } from 'firebase/auth'
import { onAuthStateChanged } from 'firebase/auth'
import { useQueryClient } from '@tanstack/react-query'
import { firebaseAuth } from '@/config/firebase'
import type { CurrentUser } from '@/features/user/api'
import { syncFirebaseAuthLanguage, signOut as signOutFirebase } from './firebase'
import { resolveAuthState, type AuthState } from './resolve'

interface AuthContextValue {
  user: User | null
  authState: AuthState
  currentUser: CurrentUser | null
  loading: boolean
  refreshing: boolean
  errorMessage: string | null
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>
  refreshAuthState: () => Promise<void>
  signOut: () => Promise<void>
}

interface InternalAuthState {
  user: User | null
  authState: AuthState
  currentUser: CurrentUser | null
  loading: boolean
  refreshing: boolean
  errorMessage: string | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const [state, setState] = useState<InternalAuthState>({
    user: null,
    authState: 'notLoggedIn',
    currentUser: null,
    loading: true,
    refreshing: false,
    errorMessage: null,
  })
  const resolveSequenceRef = useRef(0)
  const activeResolveKeyRef = useRef<string | null>(null)
  const lastResolvedKeyRef = useRef<string | null>(null)

  const applyResolvedUser = useEffectEvent(async (user: User | null, mode: 'initial' | 'refresh') => {
    const resolveKey = `${mode}:${user?.uid ?? 'guest'}:${user?.emailVerified ? 'verified' : 'pending'}`
    if (lastResolvedKeyRef.current === resolveKey && mode === 'initial') {
      return
    }

    if (activeResolveKeyRef.current === resolveKey && mode === 'initial') {
      return
    }

    activeResolveKeyRef.current = resolveKey
    const resolveSequence = ++resolveSequenceRef.current

    try {
      setState((current) => ({
        ...current,
        user,
        loading: mode === 'initial',
        refreshing: mode === 'refresh',
        errorMessage: mode === 'refresh' ? current.errorMessage : null,
        currentUser: user ? current.currentUser : null,
      }))

      syncFirebaseAuthLanguage()

      const result = await resolveAuthState(user)
      if (resolveSequence !== resolveSequenceRef.current) return

      if (result.shouldSignOut) {
        await signOutFirebase()
        return
      }

      if (result.state === 'ready' && result.currentUser) {
        queryClient.setQueryData(['current-user'], result.currentUser)
      }

      if (result.state !== 'ready') {
        queryClient.removeQueries({ queryKey: ['current-user'] })
      }

      setState({
        user,
        authState: result.state,
        currentUser: result.currentUser,
        loading: false,
        refreshing: false,
        errorMessage: result.errorMessage,
      })
      lastResolvedKeyRef.current = resolveKey
    } finally {
      if (activeResolveKeyRef.current === resolveKey) {
        activeResolveKeyRef.current = null
      }
    }
  })

  useEffect(() => {
    syncFirebaseAuthLanguage()

    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      void applyResolvedUser(user, 'initial')
    })

    return unsubscribe
  }, [applyResolvedUser])

  const getIdToken = async (forceRefresh = false) => {
    const currentUser = firebaseAuth.currentUser
    if (!currentUser) return null
    return currentUser.getIdToken(forceRefresh)
  }

  const refreshAuthState = async () => {
    await applyResolvedUser(firebaseAuth.currentUser, 'refresh')
  }

  const signOut = async () => {
    lastResolvedKeyRef.current = null
    queryClient.removeQueries({ queryKey: ['current-user'] })
    await signOutFirebase()
  }

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        authState: state.authState,
        currentUser: state.currentUser,
        loading: state.loading,
        refreshing: state.refreshing,
        errorMessage: state.errorMessage,
        getIdToken,
        refreshAuthState,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
